import { capitalize } from '@utils/utils';

export type RuleWarningKind = 'forms' | 'reminders';

export type QuickFixField = 'phone' | 'dob' | 'email';

export interface RuleContactWarning {
  message: string;
  /** Fields QuickPatientInfoPopover should collect to clear the warning. */
  requiredFields: QuickFixField[];
}

interface RuleContactWarningInput {
  hasContactMethod: boolean;
  hasDob: boolean;
  /** Subject-language noun ("patient"/"client") for the forms copy. */
  subject: string;
}

/**
 * Whether a forms/reminders rule can actually be delivered to the patient,
 * and if not, what to say and which fields to collect. Mirrors the old
 * appointment form's gating: reminders need a phone or email; forms also
 * need a date of birth. Returns null when deliverable.
 */
export const getRuleContactWarning = (
  kind: RuleWarningKind,
  { hasContactMethod, hasDob, subject }: RuleContactWarningInput,
): RuleContactWarning | null => {
  if (kind === 'reminders') {
    if (hasContactMethod) return null;
    return {
      message: 'Add an email or phone number to send reminders',
      requiredFields: ['phone', 'email'],
    };
  }

  const missing: string[] = [];
  const requiredFields: QuickFixField[] = [];
  if (!hasDob) {
    missing.push('date of birth');
    requiredFields.push('dob');
  }
  if (!hasContactMethod) {
    missing.push('email or phone number');
    requiredFields.push('phone', 'email');
  }
  if (missing.length === 0) return null;

  return {
    message: `${capitalize(subject)} ${missing.join(' and ')} required to send forms`,
    requiredFields,
  };
};
